import { jsonrepair } from "jsonrepair";

import { getSpanToolName } from "../utils";

import type { SpanNode } from "@/types/spans";

export interface KeyValueRow {
  id: string;
  path: string[];
  key: string;
  value: unknown;
  depth: number;
}

export type RowValueType = "string" | "number" | "boolean" | "array" | "object" | "null";

export const rowValueType = (value: unknown): RowValueType => {
  if (value === null || value === undefined) {
    return "null";
  }
  if (Array.isArray(value)) {
    return "array";
  }
  const type = typeof value;
  if (type === "number" || type === "boolean" || type === "string") {
    return type;
  }
  return "object";
};

const parseMaybeJson = (value: unknown): unknown => {
  if (typeof value !== "string") {
    return value;
  }
  const trimmed = value.trim();
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) {
    return value;
  }
  try {
    return JSON.parse(jsonrepair(trimmed));
  } catch {
    return value;
  }
};

export const structuredValue = (value: unknown): { structured: boolean; text: string } => {
  const parsed = parseMaybeJson(value);
  const structured = parsed !== null && typeof parsed === "object";
  if (structured) {
    return { structured, text: JSON.stringify(parsed, null, 2) };
  }
  return { structured, text: value === null || value === undefined ? "" : String(value) };
};

const MAX_ROW_DEPTH = 3;

const appendRows = (rows: KeyValueRow[], entry: { path: string[]; value: unknown }) => {
  const { path, value } = entry;
  const depth = path.length - 1;
  const isNested = value !== null && typeof value === "object" && !Array.isArray(value);
  // Depth prefix: a literal dotted key ("a.b") and a nested path (a -> b) must not share a row id
  rows.push({ id: `${path.length}:${path.join(".")}`, path, key: path[depth], value, depth });
  if (isNested && depth < MAX_ROW_DEPTH) {
    for (const [childKey, childValue] of Object.entries(value as Record<string, unknown>)) {
      appendRows(rows, { path: [...path, childKey], value: childValue });
    }
  }
};

// Structured content -> table rows; scalar content -> a single unnamed row
export const toKeyValueRows = (value: unknown): KeyValueRow[] => {
  const parsed = parseMaybeJson(value);
  if (parsed === null || parsed === undefined || parsed === "") {
    return [];
  }
  if (typeof parsed !== "object" || Array.isArray(parsed)) {
    return [{ id: "1:value", path: ["value"], key: "", value: parsed, depth: 0 }];
  }
  const rows: KeyValueRow[] = [];
  for (const [key, entryValue] of Object.entries(parsed as Record<string, unknown>)) {
    appendRows(rows, { path: [key], value: entryValue });
  }
  return rows;
};

export interface SpanEntity {
  kind: "prompt" | "tool";
  name: string;
  version?: string;
  registryId?: string;
}

const attributeString = (span: SpanNode, key: string): string | undefined => {
  const value = span.attributes?.[key];
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value === "string" && value.trim()) {
    return value.trim();
  }
  return undefined;
};

// Workflow node spans carry their template as node_configurations.template
const getNodeConfigurationTemplate = (span: SpanNode): SpanEntity | null => {
  const parsed = parseMaybeJson(span.attributes?.["node_configurations"]);
  const template = (parsed as { template?: Record<string, unknown> } | null)?.template;
  if (!template || typeof template.name !== "string" || !template.name) {
    return null;
  }
  return {
    kind: "prompt",
    name: template.name,
    version: template.version_number ? String(template.version_number) : undefined,
    registryId: template.id !== null && template.id !== undefined ? String(template.id) : undefined,
  };
};

// Registry objects this span references, resolved purely from span attributes
export const getSpanPromptEntity = (span: SpanNode): SpanEntity | null => {
  // Mocked tool runs leak the internal mock-generator prompt onto tool spans — never an entity
  if (attributeString(span, "node_type") === "TOOL_EXECUTION") {
    return null;
  }
  const name =
    attributeString(span, "promptlayer.prompt.name") || attributeString(span, "prompt_name");
  if (!name) {
    return getNodeConfigurationTemplate(span);
  }
  return {
    kind: "prompt",
    name,
    version: attributeString(span, "promptlayer.prompt.version"),
    registryId: attributeString(span, "promptlayer.prompt.id"),
  };
};

export interface SubtreeToolCall {
  name: string;
  spanId: string;
  isSelectedSpan: boolean;
}

export const collectSubtreeToolCalls = (span: SpanNode): SubtreeToolCall[] => {
  const calls: SubtreeToolCall[] = [];
  const visit = (node: SpanNode) => {
    const toolName = getSpanToolName(node);
    if (toolName) {
      calls.push({
        name: toolName,
        spanId: node.span_id,
        isSelectedSpan: node.span_id === span.span_id,
      });
    }
    node.children?.forEach(visit);
  };
  visit(span);
  return calls;
};
